import React, { useCallback, useState, useEffect, lazy, Suspense } from "react";
import { Container } from "react-bootstrap";
import { connect } from "react-redux";

import {
  emptyString,
  surveyQueuesConstants,
} from "../../constants";
import { surveyQueuesActions } from "../../redux/actions";
import {
  useSpinner,
  Spinner,
  ExpandableTable,
  SearchComponent,
  datePreferenceUtility,
  convertToDateToUnix,
  convertFromDateToUnix,
  paginatedPostRequestBody,
} from "../../../../shared";
import FilterComponent from "./filterComponent";
import { tableColumnsHeader } from "./utility";

const SurveyQueuesEmailSendModal = lazy(() =>
  import(
    "../../../../shared/components/popups/surveyQueuesEmailSend/surveyQueuesEmailSendModal"
  )
);

const SurveyQueues = (props) => {
  const {
    getSurveyQueues,
    surveyQueuesData,
    sendEmailMessage,
    surveyQueuesPaginationData,
  } = props;

  const [isOpen, setIsOpen] = useState(false);
  const [queryString, setQueryString] = useState(emptyString);
  const [previewRowData, setPreviewRowData] = useState(null);
  const [searchString, setSearchString] = useState(emptyString);
  const [selectedSubject, setSelectedSubject] = useState(emptyString);
  const [dateState, setDateState] = useState(datePreferenceUtility());
  const createDateFrom = convertFromDateToUnix(dateState.startDate);
  const createDateTo = convertToDateToUnix(dateState.endDate);

  //set pagination details with request body
  const getPaginatedRequestBody = (pageNumber) => {
    return paginatedPostRequestBody(
      surveyQueuesPaginationData,
      pageNumber,
      searchString,
      {
        id: "createdAt",
        desc: true,
      },
      queryString
    );
  };

  // Handle row preview and open modal
  const handlePreviewRow = (row) => {
    setPreviewRowData({ ...row });
    togglePreviewModal();
  };

  // Generic function to reset preview data
  const resetPreviewData = () => {
    setPreviewRowData(null);
  };

  const handleSendEmail = (emailId) => {
    sendEmailMessage(emailId, togglePreviewModal);
  };

  // Modal toggle.
  const togglePreviewModal = useCallback(() => {
    setIsOpen((prevIsOpen) => {
      const isOpenState = !prevIsOpen;
      if (!isOpenState) {
        resetPreviewData();
      }
      return isOpenState;
    });
  }, []);

  //Get table data with pagination
  const getData = (pageNumber = null) => {
    const requestBody = getPaginatedRequestBody(pageNumber);
    getSurveyQueues(requestBody);
  };

  // Show loading while a request is running.
  const spinner = useSpinner();

  //Get data on first render also on filter and search change
  useEffect(() => {
    if (queryString || searchString) {
      getData(0);
    }
    // eslint-disable-next-line
  }, [searchString, queryString]);

  useEffect(() => {
    let query = `(createdAt > ${createDateFrom} and createdAt <= ${createDateTo})`;

    if (selectedSubject) {
      const subjectQuery = `(subject="${selectedSubject}")`;
      query = `${subjectQuery} AND ${query}`;
    }

    setQueryString(query);
    // eslint-disable-next-line
  }, [createDateFrom, createDateTo, selectedSubject]);

  return (
    <>
      <FilterComponent
        refreshData={getData}
        dateState={dateState}
        setDateState={setDateState}
        selectedSubject={selectedSubject}
        setSelectedSubject={setSelectedSubject}
      />
      <Container fluid className="py-3">
        <div className="bg-white shadow rounded">
          <div className="d-flex justify-content-between align-items-center ps-4 pt-4">
            <h5 className="">Survey Queues:</h5>
            <SearchComponent
              searchString={searchString}
              setSearchString={setSearchString}
            />
          </div>
          <hr className="mx-4" />
          {spinner(surveyQueuesConstants.GET_SURVEY_QUEUES) ? (
            <Spinner />
          ) : (
            <div className="px-4">
              <div className="h-50 overflow-auto my-3">
                <Suspense
                  fallback={<div>Loading Survey Queues Email Modal...</div>}
                >
                  <SurveyQueuesEmailSendModal
                    isOpen={isOpen}
                    spinner={spinner}
                    isSumbit={handleSendEmail}
                    previewRowData={previewRowData}
                    handleSendEmail={handleSendEmail}
                    togglePreviewModal={togglePreviewModal}
                  />
                </Suspense>
              </div>

              <ExpandableTable
                perPage={[10]}
                columns={tableColumnsHeader(handlePreviewRow)}
                expandableRows={false}
                data={surveyQueuesData || []}
                keyField="surveyQueues"
                handlePageChange={(pageNum) => {
                  getData(pageNum);
                }}
                pageNumber={surveyQueuesPaginationData.number + 1}
                totalRows={surveyQueuesPaginationData.totalElements}
                numberOfRowsPerPage={10}
              />
            </div>
          )}
        </div>
      </Container>
    </>
  );
};

const mapStateToProps = (state) => ({
  surveyQueuesPaginationData:
    state.surveyQueuesReducer.surveyQueuesPaginationData,
  surveyQueuesData: state.surveyQueuesReducer.surveyQueuesData,
});

const mapDispatchToProps = (dispatch) => ({
  getSurveyQueues: (body) =>
    dispatch(surveyQueuesActions.getSurveyQueues(body)),
  sendEmailMessage: (emailId, callBackFunc) =>
    dispatch(surveyQueuesActions.sendEmailMessage(emailId, callBackFunc)),
});

export default connect(mapStateToProps, mapDispatchToProps)(SurveyQueues);
